import { useCallback } from 'react';
import * as React from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { ReactComponent as ChevronDownIcon } from 'component-lib/atoms/icon-v2/ChevronDownIcon.svg';

import { useAccordionContext } from './AccordionContext';
import { useAccordionItemContext } from './AccordionItemContext';

export interface AccordionHeaderProps {
  /**
   * The element or component the `AccordionHeader` should render as.
   */
  as?: React.ElementType;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Label for a section of content that also serves as a control for showing and
 * hiding the section of content.
 */
export function AccordionHeader({ as, children, className }: AccordionHeaderProps) {
  const { focusFirstButton, focusLastButton, focusNextButton, focusPreviousButton } =
    useAccordionContext('AccordionHeader');
  const { buttonID, isExpanded, panelID, setIsExpanded } = useAccordionItemContext('AccordionHeader');

  const handleClick = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded);
  }, [setIsExpanded]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target instanceof HTMLElement) {
        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            event.preventDefault();
            focusNextButton(event.target);
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            event.preventDefault();
            focusPreviousButton(event.target);
            break;
          case 'End':
            event.preventDefault();
            focusLastButton();
            break;
          case 'Home':
            event.preventDefault();
            focusFirstButton();
            break;
        }
      }
    },
    [focusFirstButton, focusLastButton, focusNextButton, focusPreviousButton],
  );

  return (
    <Polymorphic as={as} className={className}>
      <button
        aria-controls={panelID}
        aria-expanded={isExpanded}
        id={buttonID}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tw="bg-blue-light border-[#58647a] border-b-1 cursor-pointer flex h-6 items-center justify-between px-3 w-full focus:outline-none focus-visible:(shadow-outline z-1)"
        type="button"
      >
        <span tw="overflow-ellipsis overflow-hidden whitespace-nowrap">{children}</span>
        <ChevronDownIcon
          aria-hidden
          css={[tw`duration-fast ease-in-out flex-shrink-0 ml-1 transition`, isExpanded && tw`transform -rotate-180`]}
        />
      </button>
    </Polymorphic>
  );
}

const Polymorphic = styled.h2(tw`text-base text-white`);
